import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api';
import { Dialog } from '../../components/Dialog';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { deQty, parseQty } from '../formular';
import { fehlerText, tarifFehler } from '../tarifHinweis';
import { VorschlagInput } from '../Vorschlaege';
import {
  buchenUrl,
  fruehestes,
  mengeText,
  passtArtikel,
  vorschlagUrl,
  type AusgabeVorschlag,
  type LagerArtikel,
  type PositionVorschlag,
} from './LagerDaten';
import './lager.css';

// Zugang (Lieferung kam, Rücksendung) oder Abgang (Bruch, Eigenbedarf, Muster) von Hand für mehrere Artikel auf einmal
// (POST …/lager/buchen). Jeder Artikel nur einmal je Buchung, bis 200 Zeilen; Datum vom 1. Januar des Vorjahres bis
// heute; beim Abgang ist der Grund Pflicht. Am Handy als ganze Seite (lager.css).
// Props: art, firmId, heute (Overview.today), artikel (Lagerliste: nur Artikel mit „Bestand führen“), start (Artikel in
// Zeile 1, aus dem Verlauf), ausgabeId (aus „Ins Lager übernehmen“: Positionen der Ausgabe als Vorschlag, je Position
// ein Haken; nur angehakte Positionen buchen), onGebucht (nach dem Buchen: Liste neu laden).

const MAX_ZEILEN = 200;
interface Zeile {
  key: number;
  artikelId: string | null;
  menge: string;
  // Position der E-Rechnung (Zugang aus einer Ausgabe); ohne: Zeile von Hand
  quelle?: PositionVorschlag;
  // Haken „übernehmen“ (nur mit quelle): Artikel wählen oder Menge ändern setzt ihn
  an?: boolean;
}
// wird gebucht: Zeile aus der Ausgabe mit Haken, Zeile von Hand mit Artikel oder Menge
const gesendet = (z: Zeile) => (z.quelle ? !!z.an : !!(z.artikelId || z.menge.trim()));
let naechster = 1;
const neueZeile = (artikelId: string | null = null): Zeile => ({ key: naechster++, artikelId, menge: '' });

export function BuchenDialog({
  open,
  art,
  firmId,
  heute,
  artikel,
  start,
  ausgabeId,
  onClose,
  onGebucht,
}: {
  open: boolean;
  art: 'zugang' | 'abgang';
  firmId: string;
  heute: string;
  artikel: LagerArtikel[];
  start?: string | null;
  ausgabeId?: string | null;
  onClose(): void;
  onGebucht(): void;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={art === 'zugang' ? 'Zugang buchen' : 'Abgang buchen'} className="lg-dlg">
      {open && (
        <Formular
          art={art}
          firmId={firmId}
          heute={heute}
          artikel={artikel}
          start={start ?? null}
          ausgabeId={art === 'zugang' ? (ausgabeId ?? null) : null}
          onClose={onClose}
          onGebucht={onGebucht}
        />
      )}
    </Dialog>
  );
}

function Formular({
  art,
  firmId,
  heute,
  artikel,
  start,
  ausgabeId,
  onClose,
  onGebucht,
}: {
  art: 'zugang' | 'abgang';
  firmId: string;
  heute: string;
  artikel: LagerArtikel[];
  start: string | null;
  ausgabeId: string | null;
  onClose(): void;
  onGebucht(): void;
}) {
  const toast = useToast();
  const [zeilen, setZeilen] = useState<Zeile[]>(() => [neueZeile(start && artikel.some((a) => a.id === start) ? start : null)]);
  const [datum, setDatum] = useState(heute);
  const [notiz, setNotiz] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Zugang aus einer Ausgabe: Vorschlag des Servers; ohne (Fehler beim Laden) geht es von Hand weiter, ohne Bezug
  const [v, setV] = useState<AusgabeVorschlag | null>(null);
  const [laedt, setLaedt] = useState(!!ausgabeId);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  // „schon gebucht“ (409): Meldung mit „Trotzdem buchen“
  const [doppelt, setDoppelt] = useState<string | null>(null);
  const zugang = art === 'zugang';
  const gesperrt = !!v && (v.ausgabe.storniert || v.ausgabe.gutschrift);

  useEffect(() => {
    if (!ausgabeId) return;
    let weg = false;
    api.get<AusgabeVorschlag>(vorschlagUrl(firmId, ausgabeId)).then(
      (x) => {
        if (weg) return;
        const imLager = (id: string | undefined) => (id && artikel.some((a) => a.id === id) ? id : null);
        setV(x);
        setZeilen(
          x.positionen.length
            ? x.positionen.map((p) => ({
                key: naechster++,
                artikelId: imLager(p.vorschlag?.artikelId),
                // Einheit passt nicht: Menge selbst eintragen (nie umgerechnet)
                menge: p.einheitPasst === false ? '' : deQty(p.menge),
                quelle: p,
                an: false,
              }))
            : [neueZeile()],
        );
        setDatum(x.datum);
        setNotiz(x.notiz);
        setLaedt(false);
      },
      (err: unknown) => {
        if (weg) return;
        setLadeFehler(fehlerText(err));
        setLaedt(false);
      },
    );
    return () => {
      weg = true;
    };
  }, [firmId, ausgabeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setZeile = (key: number, patch: Partial<Zeile>) => setZeilen((cur) => cur.map((z) => (z.key === key ? { ...z, ...patch } : z)));
  // Zeile in Meldungen: Position der Rechnung bzw. Nummer in der Liste
  const name = (z: Zeile) => (z.quelle ? `Position ${z.quelle.pos}` : `Zeile ${zeilen.indexOf(z) + 1}`);

  async function buchen(e: FormEvent | null, trotzdem = false) {
    e?.preventDefault();
    setFehler(null);
    setDoppelt(null);
    // leere Zeilen (ohne Artikel und Menge) und Positionen ohne Haken fallen weg
    const voll = zeilen.filter(gesendet);
    if (!voll.length) return setFehler(v ? 'Bitte mindestens eine Position übernehmen (Haken) oder einen Artikel eintragen.' : 'Bitte mindestens einen Artikel eintragen.');
    for (const z of voll) {
      if (!z.artikelId) return setFehler(`${name(z)}: Bitte einen Artikel wählen.`);
      if (parseQty(z.menge) === undefined) return setFehler(`${name(z)}: Bitte die Menge prüfen, z. B. 1 oder 2,5.`);
    }
    if (!datum || datum < fruehestes(heute) || datum > heute) return setFehler('Bitte das Datum prüfen.');
    if (!zugang && !notiz.trim()) return setFehler('Bitte den Grund eintragen, z. B. Bruch.');
    setBusy(true);
    try {
      const r = await api.post<{ anzahl: number; ausgabe: string | null }>(buchenUrl(firmId), {
        art,
        datum,
        notiz: notiz.trim() || null,
        zeilen: voll.map((z) => ({ artikelId: z.artikelId, menge: parseQty(z.menge) })),
        ...(v && ausgabeId ? { ausgabeId, trotzdem } : {}),
      });
      const n = r.anzahl;
      toast(`${n} ${zugang ? (n === 1 ? 'Zugang' : 'Zugänge') : n === 1 ? 'Abgang' : 'Abgänge'}${r.ausgabe ? ` aus Ausgabe ${r.ausgabe}` : ''} gebucht`);
      onGebucht();
      onClose();
    } catch (err) {
      // Tarif: das Fenster „Mehr mit Billtoro“ zeigt es (dieses schließen, es kann nur eines offen sein)
      if (tarifFehler(err)) onClose();
      else if (err instanceof ApiError && err.code === 'schon_gebucht') setDoppelt(err.message);
      else setFehler(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  if (laedt) return <div className="spinner" aria-label="Lädt" />;

  return (
    <form onSubmit={(e) => void buchen(e)} noValidate className="lg-buchen">
      {v ? (
        <>
          <p className="lg-klein">
            Aus Ausgabe {v.ausgabe.nummer} ({v.ausgabe.partner}): Haken je Position setzen, Artikel und Menge prüfen. Billtoro schlägt nur vor.
          </p>
          {gesperrt ? (
            <div className="notice bad" role="alert">
              {v.hinweise.join(' ')} Einen Zugang von Hand buchen Sie über „Zugang“ auf der Lagerseite.
            </div>
          ) : (
            v.hinweise.map((h) => (
              <p key={h} className="muted lg-klein">
                {h}
              </p>
            ))
          )}
          {!gesperrt && v.gebucht.length > 0 && (
            <p className="notice warn">
              Aus dieser Ausgabe wurde schon gebucht:{' '}
              {v.gebucht.map((g) => `Nr. ${g.nr} vom ${fmtDate(g.datum)} (${g.artikel}, ${mengeText(g.menge, g.einheit)})`).join('; ')}.
            </p>
          )}
        </>
      ) : (
        <p className="muted lg-klein">
          {zugang ? 'Neue Ware, Rücksendung: die Menge kommt zum Bestand dazu.' : 'Bruch, Eigenbedarf, Muster: die Menge geht vom Bestand ab.'}
        </p>
      )}
      {ladeFehler && <div className="notice bad">{ladeFehler} Sie können die Artikel von Hand eintragen.</div>}
      {!gesperrt && (
        <>
          <ol className="lg-zeilen">
            {zeilen.map((z, i) => {
              const a = z.artikelId ? artikel.find((x) => x.id === z.artikelId) : undefined;
              // nur Zeilen, die gebucht würden, belegen einen Artikel (ein vorgeschlagener ohne Haken bleibt wählbar)
              const andere = new Set(zeilen.filter((x) => x.key !== z.key && x.artikelId && (!x.quelle || x.an)).map((x) => x.artikelId));
              return (
                <li key={z.key} className="lg-zeile-b">
                  {z.quelle && (
                    <label className="lg-pos">
                      <input
                        type="checkbox"
                        checked={!!z.an}
                        onChange={(e) => setZeile(z.key, { an: e.target.checked })}
                        aria-label={`Position ${z.quelle.pos} übernehmen`}
                      />
                      <span>
                        Position {z.quelle.pos}: {z.quelle.text || 'ohne Text'} · {mengeText(z.quelle.menge, z.quelle.einheitText ?? '')}
                        {z.quelle.artikelnummer && <span className="muted"> · Art.-Nr. {z.quelle.artikelnummer}</span>}
                        {z.quelle.hinweis && <small className="lg-pos-hinweis">{z.quelle.hinweis}</small>}
                      </span>
                    </label>
                  )}
                  <div className="field lg-art">
                    <span>Artikel</span>
                    <ArtikelWahl
                      artikel={artikel.filter((x) => !andere.has(x.id))}
                      gewaehlt={a ?? null}
                      n={i + 1}
                      onChange={(id) => setZeile(z.key, { artikelId: id, ...(z.quelle && id ? { an: true } : {}) })}
                    />
                  </div>
                  <label className="field lg-menge">
                    <span>Menge</span>
                    <span className="lg-menge-in">
                      <input
                        value={z.menge}
                        inputMode="decimal"
                        aria-label={`Menge ${i + 1}`}
                        placeholder="z. B. 10"
                        onChange={(e) => setZeile(z.key, { menge: e.target.value, ...(z.quelle ? { an: true } : {}) })}
                      />
                      {a && <span className="muted">{a.unit}</span>}
                    </span>
                  </label>
                  <button
                    type="button"
                    className="rt-del lg-weg"
                    aria-label={`Zeile ${i + 1} entfernen`}
                    disabled={zeilen.length === 1}
                    onClick={() => setZeilen((cur) => cur.filter((x) => x.key !== z.key))}
                  >
                    ×
                  </button>
                  {a && (
                    <p className="lg-jetzt muted">
                      Bestand jetzt: <span className="num">{mengeText(a.bestand, a.unit)}</span>
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
          {zeilen.length < MAX_ZEILEN && (
            <button type="button" className="linkbtn" onClick={() => setZeilen((cur) => [...cur, neueZeile()])}>
              + Zeile
            </button>
          )}
          <div className="lg-zwei">
            <label className="field">
              <span>Datum</span>
              <input type="date" value={datum} min={fruehestes(heute)} max={heute} onChange={(e) => setDatum(e.target.value)} />
            </label>
            <label className="field">
              <span>{zugang ? 'Notiz (optional)' : 'Grund'}</span>
              <input
                value={notiz}
                maxLength={200}
                placeholder={zugang ? 'z. B. Lieferant XY, LS 4711' : 'z. B. Bruch, Eigenbedarf, Muster'}
                onChange={(e) => setNotiz(e.target.value)}
              />
            </label>
          </div>
        </>
      )}
      {fehler && (
        <div className="notice bad" role="alert">
          {fehler}
        </div>
      )}
      {doppelt && (
        <div className="notice warn" role="alert">
          {doppelt} Noch einmal buchen?{' '}
          <button type="button" className="linkbtn" disabled={busy} onClick={() => void buchen(null, true)}>
            Trotzdem buchen
          </button>
        </div>
      )}
      <div className="dlg-actions lg-dlg-act">
        <button type="button" className="btn" onClick={onClose}>
          Abbrechen
        </button>
        <button type="submit" className="btn primary" disabled={busy || gesperrt}>
          Buchen
        </button>
      </div>
    </form>
  );
}

// Artikel wählen: Suche nach Name oder Artikelnummer (nur Artikel aus dem Lager); gewählt: Name mit „ändern“
export function ArtikelWahl({
  artikel,
  gewaehlt,
  n,
  onChange,
}: {
  artikel: LagerArtikel[];
  gewaehlt: LagerArtikel | null;
  n: number;
  onChange(id: string | null): void;
}) {
  const [text, setText] = useState('');
  const treffer = useMemo(() => artikel.filter((a) => passtArtikel(a, text)).slice(0, 8), [artikel, text]);
  if (gewaehlt) {
    return (
      <span className="lg-gewaehlt">
        <b>{gewaehlt.name}</b>
        {gewaehlt.artikelnummer && <span className="muted num">{gewaehlt.artikelnummer}</span>}
        <button
          type="button"
          className="linkbtn"
          aria-label={`Artikel ${n}: anderen Artikel wählen`}
          onClick={() => {
            setText('');
            onChange(null);
          }}
        >
          ändern
        </button>
      </span>
    );
  }
  return (
    <VorschlagInput
      value={text}
      vorschlaege={treffer.map((a) => ({
        key: a.id,
        label: (
          <>
            {a.artikelnummer && <span className="num lg-nr">{a.artikelnummer}</span>} {a.name}
          </>
        ),
        sub: `Bestand ${mengeText(a.bestand, a.unit)}`,
      }))}
      onPick={(id) => onChange(id)}
      onChange={setText}
      inputProps={{ 'aria-label': `Artikel ${n}`, placeholder: 'Suchen: Name oder Nummer' }}
    />
  );
}
